import { useTranslate } from '../../application/i18n/LocaleProvider';
import { cardOf } from '../../domain/game/overtakeDeck';
import OvertakeCard from './OvertakeCard';

/**
 * The reader's own cards, fanned (SPEC-011 D-15, PR-11.5).
 *
 * Every card that cannot be played right now is drawn as unplayable rather than
 * hidden or removed. Which cards are legal is the most interesting information
 * on the screen — a hand where four of five have gone flat is the whole reason
 * to consider a counter-call — and a hand that quietly dropped them would be
 * answering the reader's question by deleting it.
 *
 * A ROW, NOT A FAN (D-29). PR-11.5 fanned it, on the argument that a hand is
 * held rather than laid out, and the argument was right about a hand and wrong
 * about this screen: a fan overlaps five cards to the width of about three, and
 * every card it hides is a figure the reader is supposed to be choosing between.
 * Laid out flat they are all readable at once, the row is half the height the
 * fan needed, and what that buys is the whole table above it on the same screen.
 * The step, the rotation and the pan go with it.
 *
 * A HAND IS NOT ALWAYS FIVE. `advanceRound` gives the passer a slipstream draw
 * before it refills, so a player who is led at and passes ends the round holding
 * six. Nothing here counts or divides by the number of cards — the row is a grid
 * of equal columns and the cards narrow together — so a hand of eight is drawn
 * by the same rule as a hand of five.
 *
 * IT IS CAPTIONED, and the caption carries the count. That line used to be a
 * chip beside the prompt at the foot of the screen, which is a long way from the
 * cards it counts; over them it also does the job the fan's own outline did, of
 * saying where the reader's own cards begin.
 */
export default function OvertakeHand({
    ids = [],
    deck,
    spec,
    playable = [],
    selected = null,
    onSelect,
    isDisabled = false,
}) {
    const t = useTranslate();
    const legal = new Set(playable);

    if (ids.length === 0) {
        return <p className="overtake-hand__empty">{t('play.hand.empty')}</p>;
    }

    return (
        <section className="overtake-hand-band">
            <h2 className="overtake-hand__title">
                {t('play.hand.title', { count: ids.length })}
            </h2>

            <ul className="overtake-hand" aria-label={t('play.hand')}>
                {ids.map((id) => (
                    <li className="overtake-hand__slot" key={id}>
                        <OvertakeCard
                            car={cardOf(deck, id)}
                            spec={spec}
                            isPlayable={!isDisabled && legal.has(id)}
                            isSelected={id === selected}
                            onSelect={onSelect}
                        />
                    </li>
                ))}
            </ul>
        </section>
    );
}
